import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { RefreshCw, Save, RotateCcw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function PayrollConfiguration({ user, onLogout }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    setDataLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/hr-config/payroll`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig(response.data);
      setHasChanges(false);
    } catch (error) {
      console.error('Error fetching config:', error);
      // Set default values if not configured
      setConfig({
        basic_percentage: 40.0,
        hra_percentage_of_basic: 50.0,
        da_percentage: 20.0,
        special_allowance_percentage: 40.0,
        conveyance_allowance: 1600.0,
        medical_allowance: 1250.0,
        pf_employee_percentage: 12.0,
        pf_employer_percentage: 12.0,
        esi_employee_percentage: 0.75,
        esi_employer_percentage: 3.25,
        esi_threshold: 21000,
        professional_tax: 200,
        payment_date: 10
      });
    } finally {
      setDataLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setConfig({ ...config, [field]: value });
    setHasChanges(true);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/hr-config/payroll`, config, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Payroll configuration updated successfully!');
      setHasChanges(false);
      fetchConfig();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  if (dataLoading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="p-6 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
            <p className="mt-3 text-gray-500">Loading payroll configuration...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>Payroll Configuration</h1>
            <p className="text-gray-600 mt-2">Configure salary components, deductions, and compliance thresholds</p>
          </div>
          <button
            onClick={fetchConfig}
            disabled={dataLoading}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${dataLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Earnings Configuration */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold mb-4 text-green-700">Earnings Configuration</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Basic Salary (% of CTC)</label>
                <input
                  type="number"
                  step="0.1"
                  value={config?.basic_percentage || 0}
                  onChange={(e) => handleChange('basic_percentage', parseFloat(e.target.value))}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">HRA (% of Basic)</label>
                <input
                  type="number"
                  step="0.1"
                  value={config?.hra_percentage_of_basic || 0}
                  onChange={(e) => handleChange('hra_percentage_of_basic', parseFloat(e.target.value))}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">DA (% of Basic)</label>
                <input
                  type="number"
                  step="0.1"
                  value={config?.da_percentage || 0}
                  onChange={(e) => handleChange('da_percentage', parseFloat(e.target.value))}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Special Allowance (%)</label>
                <input
                  type="number"
                  step="0.1"
                  value={config?.special_allowance_percentage || 0}
                  onChange={(e) => handleChange('special_allowance_percentage', parseFloat(e.target.value))}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Conveyance Allowance (Fixed)</label>
                <input
                  type="number"
                  value={config?.conveyance_allowance || 0}
                  onChange={(e) => handleChange('conveyance_allowance', parseFloat(e.target.value))}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Medical Allowance (Fixed)</label>
                <input
                  type="number"
                  value={config?.medical_allowance || 0}
                  onChange={(e) => handleChange('medical_allowance', parseFloat(e.target.value))}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Deductions Configuration */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold mb-4 text-red-700">Deductions Configuration</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">PF Employee (%)</label>
                <input
                  type="number"
                  step="0.1"
                  value={config?.pf_employee_percentage || 0}
                  onChange={(e) => handleChange('pf_employee_percentage', parseFloat(e.target.value))}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">PF Employer (%)</label>
                <input
                  type="number"
                  step="0.1"
                  value={config?.pf_employer_percentage || 0}
                  onChange={(e) => handleChange('pf_employer_percentage', parseFloat(e.target.value))}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">ESI Employee (%)</label>
                <input
                  type="number"
                  step="0.01"
                  value={config?.esi_employee_percentage || 0}
                  onChange={(e) => handleChange('esi_employee_percentage', parseFloat(e.target.value))}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">ESI Employer (%)</label>
                <input
                  type="number"
                  step="0.01"
                  value={config?.esi_employer_percentage || 0}
                  onChange={(e) => handleChange('esi_employer_percentage', parseFloat(e.target.value))}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">ESI Threshold</label>
                <input
                  type="number"
                  value={config?.esi_threshold || 0}
                  onChange={(e) => handleChange('esi_threshold', parseInt(e.target.value))}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">ESI applicable if gross salary is below this amount</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Professional Tax</label>
                <input
                  type="number"
                  value={config?.professional_tax || 0}
                  onChange={(e) => handleChange('professional_tax', parseInt(e.target.value))}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Payment Date (Day of Month)</label>
                <input
                  type="number"
                  min="1"
                  max="28"
                  value={config?.payment_date || 10}
                  onChange={(e) => handleChange('payment_date', parseInt(e.target.value))}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Summary Card */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-6">
          <h3 className="font-bold mb-3" style={{color: '#2b3380'}}>Current Configuration Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-gray-600">Basic</p>
              <p className="font-bold">{config?.basic_percentage || 0}%</p>
            </div>
            <div>
              <p className="text-gray-600">HRA</p>
              <p className="font-bold">{config?.hra_percentage_of_basic || 0}% of Basic</p>
            </div>
            <div>
              <p className="text-gray-600">PF</p>
              <p className="font-bold">{config?.pf_employee_percentage || 0}% + {config?.pf_employer_percentage || 0}%</p>
            </div>
            <div>
              <p className="text-gray-600">Payment Date</p>
              <p className="font-bold">{config?.payment_date || 10}th of month</p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        {hasChanges && (
          <div className="mt-6 flex gap-4 sticky bottom-6">
            <button
              onClick={handleSave}
              disabled={loading}
              className="px-6 py-3 text-white rounded-lg flex items-center gap-2 hover:opacity-90 transition"
              style={{backgroundColor: '#2b3380'}}
            >
              <Save className="w-5 h-5" />
              {loading ? 'Saving...' : 'Save Configuration'}
            </button>
            <button
              onClick={fetchConfig}
              className="px-6 py-3 bg-gray-400 text-white rounded-lg flex items-center gap-2 hover:bg-gray-500 transition"
            >
              <RotateCcw className="w-5 h-5" />
              Reset Changes
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default PayrollConfiguration;
